import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { FdaUdiModel } from '../../../../models/data-sections/fda/udiModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface UdiModalProps { searchExecutionId: string; selected_udi: FdaUdiModel; }

const UdiModal: React.FC<UdiModalProps> = ({
    searchExecutionId,
    selected_udi,
}) => {
    const [udiData, setUdiData] = useState<FdaUdiModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didErrorOccur, setDidErrorOccur] = useState(false);
    const { triggerSystemError } = useSystemError();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await fetchUdiDetail(searchExecutionId, selected_udi.public_device_record_key, new Date(selected_udi.__ci_system_search_date_key ?? 0));
            }
            catch (error) {
                console.error('Error during fetch:', error);
            }
            finally {
                setIsLoading(false);
            }
        }; fetchData();
    }, [searchExecutionId, selected_udi]);

    const fetchUdiDetail = async (search_execution_id: string, public_device_record_key: string, __ci_system_search_date_key: Date): Promise<void> => {
        try {
            const response = await fdaApi.getUdiDetail(search_execution_id, public_device_record_key, __ci_system_search_date_key);
            if (response.success) {
                const data = (response as ResponseSuccessModel).data as FdaUdiModel[];
                setUdiData(data?.[0] || null);
            } else {
                handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
            }
        } catch (error) {
            handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Typography variant="h6">{udiData?.device_description || 'UDI Device Record'}</Typography>
                <Typography variant="subtitle2" color="textSecondary">{selected_udi.public_device_record_key} / {selected_udi.company_name}</Typography>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px"><CircularProgress /></Box>
            ) : didErrorOccur || !udiData ? (
                <Typography color="error">Failed to load UDI details.</Typography>
            ) : (
                <Box mt={2}>
                    <Typography><strong>Company:</strong> {udiData.company_name || 'N/A'}</Typography>
                    <Typography><strong>Device Description:</strong> {udiData.device_description || 'N/A'}</Typography>
                    <Typography><strong>Labeler DUNS:</strong> {udiData.labeler_duns_number || 'N/A'}</Typography>
                    <Typography><strong>Commercial Distribution Status:</strong> {udiData.commercial_distribution_status || 'N/A'}</Typography>
                    <Typography><strong>Kit:</strong> {udiData.is_kit || 'N/A'}</Typography>
                    <Typography><strong>Rx Only:</strong> {udiData.is_rx || 'N/A'}</Typography>
                    <Typography><strong>OTC:</strong> {udiData.is_otc || 'N/A'}</Typography>
                    <Typography><strong>Single Use:</strong> {udiData.is_single_use || 'N/A'}</Typography>
                    <Typography><strong>PM Exempt:</strong> {udiData.is_pm_exempt || 'N/A'}</Typography>
                    <Typography><strong>Has Serial Number:</strong> {udiData.has_serial_number || 'N/A'}</Typography>
                    <Typography><strong>Has Lot/Batch Number:</strong> {udiData.has_lot_or_batch_number || 'N/A'}</Typography>
                    <Typography><strong>Has Expiration Date:</strong> {udiData.has_expiration_date || 'N/A'}</Typography>
                    <Typography><strong>Has Manufacturing Date:</strong> {udiData.has_manufacturing_date || 'N/A'}</Typography>

                    {udiData.mri_safety && <Typography><strong>MRI Safety:</strong> {udiData.mri_safety}</Typography>}

                    {Array.isArray(udiData.gmdn_terms) && udiData.gmdn_terms.length > 0 && (
                        <Box mt={3}>
                            <Typography variant="h6">GMDN Terms</Typography>
                            {udiData.gmdn_terms.map((term, idx) => (
                                <Typography key={idx} paragraph>{term.name} ({term.code}): {term.definition}</Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default UdiModal;
